import { Link } from 'react-router-dom';

import PageHeader from '../../../common/components/page-header/page-header';
import Section from '../../../common/components/section/section';
import SegmentedBar from '../../../common/components/segmented-bar/segmented-bar';
import StatusPill from '../../../common/components/status-pill/status-pill';
import EmptyState from '../../../common/components/state-message/empty-state';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import Tag from '../../../common/components/tag/tag';
import RelativeTime from '../../../common/components/relative-time/relative-time';
import {
    PHASE_LABEL,
    PHASE_ORDER,
    PLAN_STATE_LABEL,
    PLAN_STATE_TONE,
    PROJECT_STATE_LABEL,
    PROJECT_STATE_TONE,
    REPOSITORY_KIND_LABEL,
} from '../../../core/constants/domain';
import { ROUTES } from '../../../core/constants/routes';
import { useTasksList } from '../../../global/contexts/work/work';
import { useProject, useProjectPlans } from '../contexts/projects';
import { groupByTier, summarizeProgress } from '../utils/progress';

import '../styles/detail.css';

function PhaseTrack({ phase }) {
    const current = PHASE_ORDER.indexOf(phase);
    return (
        <ol className="project-detail__phases" aria-label={`Fase: ${PHASE_LABEL[phase] ?? phase}`}>
            {PHASE_ORDER.map((step, index) => (
                <li
                    key={step}
                    className={[
                        'project-detail__phase',
                        index < current && 'project-detail__phase--past',
                        index === current && 'project-detail__phase--current',
                    ].filter(Boolean).join(' ')}
                    aria-current={index === current ? 'step' : undefined}
                    title={PHASE_LABEL[step]}
                />
            ))}
        </ol>
    );
}

function TierRow({ tier, tasks, projectName }) {
    const progress = summarizeProgress(tasks);
    return (
        <li className="project-detail__tier">
            <div className="project-detail__tier-head">
                <span className="project-detail__tier-name">{tier.name}</span>
                <span className="project-detail__tier-percent">{progress.total ? `${progress.percent}%` : '—'}</span>
            </div>
            <PhaseTrack phase={tier.phase} />
            <p className="project-detail__tier-meta">
                <span>{PHASE_LABEL[tier.phase] ?? tier.phase}</span>
                <span>{progress.total ? `${progress.done}/${progress.total} tareas` : 'Sin tareas todavía'}</span>
                {tier.state === 'closed' && <span>Cerrado</span>}
            </p>
            <SegmentedBar segments={progress.segments} label={`Avance de ${tier.name} en ${projectName}`} />
        </li>
    );
}

export default function ProjectDetail({ projectId }) {
    const projectQuery = useProject(projectId);
    const plansQuery = useProjectPlans(projectId);
    const tasksQuery = useTasksList({ projectId });

    if (projectQuery.isPending) return <LoadingState label="Cargando proyecto" rows={5} />;
    if (projectQuery.isError) {
        return (
            <>
                <PageHeader backTo={ROUTES.PROJECTS} backLabel="Proyectos" title="Proyecto" />
                <ErrorState error={projectQuery.error} title="No pudimos abrir este proyecto" onRetry={projectQuery.refetch} />
            </>
        );
    }

    const project = projectQuery.data;
    const tasks = tasksQuery.data?.items ?? [];
    const progress = summarizeProgress(tasks);
    const byTier = groupByTier(tasks);

    return (
        <div className="project-detail">
            <PageHeader
                backTo={ROUTES.PROJECTS}
                backLabel="Proyectos"
                eyebrow={project.clientName ?? 'Proyecto'}
                title={project.name}
                description={project.description}
                meta={(
                    <>
                        <StatusPill tone={PROJECT_STATE_TONE[project.state]}>{PROJECT_STATE_LABEL[project.state] ?? project.state}</StatusPill>
                        {project.bootstrapPhase && <StatusPill tone="accent" hasDot={false}>{PHASE_LABEL[project.bootstrapPhase] ?? project.bootstrapPhase}</StatusPill>}
                    </>
                )}
            />

            <section className="project-detail__summary" aria-label="Avance del proyecto">
                <p className="project-detail__big-number">{tasksQuery.isPending ? '—' : `${progress.percent}%`}</p>
                <div className="project-detail__summary-body">
                    <p>{tasksQuery.isPending ? 'Calculando avance…' : `${progress.done} de ${progress.total} tareas hechas`}</p>
                    <SegmentedBar segments={progress.segments} label={`Avance de ${project.name}`} />
                    <ul className="project-detail__legend">
                        {progress.segments.map((segment) => (
                            <li key={segment.key} className={`project-detail__legend-item project-detail__legend-item--${segment.tone}`}>
                                {segment.label} <strong>{segment.value}</strong>
                            </li>
                        ))}
                    </ul>
                    <Link to={`${ROUTES.WORK}?project=${project.id}`} className="project-detail__work-link">Ver la cola de trabajo de este proyecto</Link>
                </div>
            </section>

            <div className="project-detail__columns">
                <Section title="Tiers" count={project.tiers.length}>
                    {project.tiers.length === 0
                        ? <EmptyState title="Sin tiers" description="El proyecto todavía no tiene tiers definidos." />
                        : (
                            <ul className="project-detail__tiers">
                                {project.tiers.map((tier) => (
                                    <TierRow key={tier.id} tier={tier} tasks={byTier.get(tier.id) ?? []} projectName={project.name} />
                                ))}
                            </ul>
                        )}
                </Section>

                <div className="project-detail__side">
                    <Section title="Repositorios" count={project.repositories.length}>
                        {project.repositories.length === 0
                            ? <EmptyState title="Sin repositorios" />
                            : (
                                <ul className="project-detail__repos">
                                    {project.repositories.map((repo) => (
                                        <li key={repo.id} className="project-detail__repo">
                                            <span className="project-detail__repo-name mono">{repo.name}</span>
                                            <Tag swatch={repo.kind}>{REPOSITORY_KIND_LABEL[repo.kind] ?? repo.kind}</Tag>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </Section>

                    <Section title="Planes">
                        {plansQuery.isPending && <LoadingState label="Cargando planes" rows={1} />}
                        {plansQuery.isError && <ErrorState error={plansQuery.error} onRetry={plansQuery.refetch} />}
                        {plansQuery.isSuccess && plansQuery.data.items.length === 0 && <EmptyState title="Sin planes" />}
                        {plansQuery.isSuccess && plansQuery.data.items.map((plan) => (
                            <div key={plan.id} className="project-detail__plan">
                                <p className="project-detail__plan-name">{plan.name}</p>
                                <ul className="project-detail__versions">
                                    {plan.versions.map((version) => (
                                        <li key={version.number} className="project-detail__version">
                                            <span className="mono">v{version.number}</span>
                                            <StatusPill tone={PLAN_STATE_TONE[version.state]}>{PLAN_STATE_LABEL[version.state] ?? version.state}</StatusPill>
                                            <RelativeTime value={version.updatedAt} className="project-detail__version-time" />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </Section>

                    <Section title="Equipos asignados" count={project.teams.length}>
                        {project.teams.length === 0
                            ? <EmptyState title="Sin equipos asignados" />
                            : (
                                <ul className="project-detail__teams">
                                    {project.teams.map((team) => (
                                        <li key={team.id}>
                                            <Link to={ROUTES.TEAM(team.id)} className="project-detail__team">{team.name}</Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </Section>
                </div>
            </div>
        </div>
    );
}
